import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, CheckCircle, User, Building2, Calendar, GraduationCap, Clock, Check, X, ExternalLink, Filter, ArrowUpDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const DozentenView = () => {
  const navigate = useNavigate();
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("offen"); // offen, erledigt, alle
  const [zeitFilter, setZeitFilter] = useState("alle"); // alle, heute, diese_woche, naechste_7_tage, naechste_14_tage
  const [sortOrder, setSortOrder] = useState("asc"); // asc = nächste zuerst, desc = spätere zuerst

  useEffect(() => {
    loadTeilnehmer();
  }, []);

  const loadTeilnehmer = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/dozenten/kontaktliste`);
      const data = Array.isArray(res.data) ? res.data : [];
      setTeilnehmer(data);
    } catch (error) {
      console.error("Fehler beim Laden:", error);
      toast.error("Fehler beim Laden der Kontaktliste");
      setTeilnehmer([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleKontaktiert = async (e, teilnehmerId, kursName, currentStatus) => {
    e.stopPropagation(); // Verhindere Navigation zur Akte
    try {
      await axios.put(`${API}/api/dozenten/kontakt-status/${teilnehmerId}`, {
        kurs_name: kursName,
        kontaktiert: !currentStatus
      });
      toast.success(currentStatus ? "Als offen markiert" : "Als kontaktiert markiert");
      loadTeilnehmer();
    } catch (error) {
      console.error("Fehler:", error);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleNavigateToTeilnehmer = (teilnehmerId) => {
    // Navigiere zur Teilnehmer-Seite mit der ID als Query-Parameter
    // Die Route /teilnehmer/{id} existiert nicht, daher als Query-Parameter
    navigate(`/teilnehmer?id=${teilnehmerId}`);
  };

  // Hilfsfunktion für Datumsberechnungen
  const getDaysUntilStart = (kursStart) => {
    if (!kursStart) return null;
    const startDate = new Date(kursStart);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    return Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
  };

  // Sicheres Array
  const safeArray = Array.isArray(teilnehmer) ? teilnehmer : [];

  // Status-Filter
  let filtered = safeArray.filter(t => {
    if (statusFilter === "offen") return !t.dozent_kontaktiert;
    if (statusFilter === "erledigt") return t.dozent_kontaktiert;
    return true;
  });

  // Zeit-Filter
  filtered = filtered.filter(t => {
    const days = getDaysUntilStart(t.kurs_start);
    if (days === null) return zeitFilter === "alle";
    
    switch (zeitFilter) {
      case "heute":
        return days === 0;
      case "diese_woche":
        return days >= 0 && days <= 7;
      case "naechste_7_tage":
        return days >= 0 && days <= 7;
      case "naechste_14_tage":
        return days >= 0 && days <= 14;
      case "vergangen":
        return days < 0;
      default:
        return true;
    }
  });

  // Sortierung nach Startdatum
  const sortedTeilnehmer = [...filtered].sort((a, b) => {
    const dateA = a.kurs_start ? new Date(a.kurs_start) : new Date("9999-12-31");
    const dateB = b.kurs_start ? new Date(b.kurs_start) : new Date("9999-12-31");
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });

  // Statistiken
  const offeneCount = safeArray.filter(t => !t.dozent_kontaktiert).length;
  const erledigteCount = safeArray.filter(t => t.dozent_kontaktiert).length;
  const dringendeCount = safeArray.filter(t => {
    const days = getDaysUntilStart(t.kurs_start);
    return !t.dozent_kontaktiert && days !== null && days >= 0 && days <= 7;
  }).length;

  // Formatiere Datum für deutsche Anzeige
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="dozenten-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Phone className="h-6 w-6" />
            Teilnehmer kontaktieren
          </h1>
          <p className="text-gray-500 mt-1">
            Neue Teilnehmer vor Kursbeginn anrufen - sortiert nach Startdatum
          </p>
        </div>
        <Button onClick={loadTeilnehmer} variant="outline">
          Aktualisieren
        </Button>
      </div>

      {/* Statistik-Karten */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === "offen" && zeitFilter === "alle" ? "ring-2 ring-orange-500" : ""}`}
          onClick={() => { setStatusFilter("offen"); setZeitFilter("alle"); }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Offen</p>
                <p className="text-3xl font-bold text-orange-600">{offeneCount}</p>
              </div>
              <Clock className="h-10 w-10 text-orange-200" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${zeitFilter === "diese_woche" ? "ring-2 ring-red-500" : ""}`}
          onClick={() => { setStatusFilter("offen"); setZeitFilter("diese_woche"); }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Dringend (7 Tage)</p>
                <p className="text-3xl font-bold text-red-600">{dringendeCount}</p>
              </div>
              <Calendar className="h-10 w-10 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${statusFilter === "erledigt" ? "ring-2 ring-green-500" : ""}`}
          onClick={() => { setStatusFilter("erledigt"); setZeitFilter("alle"); }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Erledigt</p>
                <p className="text-3xl font-bold text-green-600">{erledigteCount}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${statusFilter === "alle" && zeitFilter === "alle" ? "ring-2 ring-blue-500" : ""}`}
          onClick={() => { setStatusFilter("alle"); setZeitFilter("alle"); }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gesamt</p>
                <p className="text-3xl font-bold text-blue-600">{safeArray.length}</p>
              </div>
              <User className="h-10 w-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter-Leiste */}
      <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-600">Filter:</span>
        </div>
        
        <Select value={zeitFilter} onValueChange={setZeitFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Zeitraum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Termine</SelectItem>
            <SelectItem value="heute">Heute</SelectItem>
            <SelectItem value="diese_woche">Diese Woche</SelectItem>
            <SelectItem value="naechste_7_tage">Nächste 7 Tage</SelectItem>
            <SelectItem value="naechste_14_tage">Nächste 14 Tage</SelectItem>
            <SelectItem value="vergangen">Bereits gestartet</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Status</SelectItem>
            <SelectItem value="offen">Offen</SelectItem>
            <SelectItem value="erledigt">Erledigt</SelectItem>
          </SelectContent>
        </Select>

        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
          className="flex items-center gap-1"
        >
          <ArrowUpDown className="h-4 w-4" />
          {sortOrder === "asc" ? "Nächste zuerst" : "Spätere zuerst"}
        </Button>

        <span className="text-sm text-gray-500 ml-auto">
          {sortedTeilnehmer.length} Teilnehmer
        </span>
      </div>

      {/* Tabelle */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            {statusFilter === "offen" && zeitFilter === "alle" && "Noch zu kontaktieren"}
            {statusFilter === "offen" && zeitFilter === "diese_woche" && "Dringend - Start in 7 Tagen"}
            {statusFilter === "offen" && zeitFilter === "naechste_14_tage" && "Start in 14 Tagen"}
            {statusFilter === "erledigt" && "Bereits kontaktiert"}
            {statusFilter === "alle" && zeitFilter === "alle" && "Alle Teilnehmer"}
            {zeitFilter === "vergangen" && "Kurse bereits gestartet"}
            <span className="text-gray-400 font-normal">({sortedTeilnehmer.length})</span>
          </CardTitle>
          <p className="text-xs text-gray-500">Klicken Sie auf einen Namen, um zur Teilnehmer-Akte zu gelangen</p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Laden...</div>
          ) : sortedTeilnehmer.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {statusFilter === "offen" && zeitFilter === "alle"
                ? "Alle Teilnehmer wurden kontaktiert! " 
                : "Keine Teilnehmer für diesen Filter gefunden"}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[50px]">Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Pflegeeinrichtung</TableHead>
                  <TableHead>Kurs</TableHead>
                  <TableHead className="cursor-pointer" onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}>
                    <div className="flex items-center gap-1">
                      Startdatum
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTeilnehmer.map((t) => {
                  const daysUntilStart = getDaysUntilStart(t.kurs_start);
                  const isUrgent = daysUntilStart !== null && daysUntilStart >= 0 && daysUntilStart <= 7;
                  const isVeryUrgent = daysUntilStart !== null && daysUntilStart >= 0 && daysUntilStart <= 3;
                  const isPast = daysUntilStart !== null && daysUntilStart < 0;

                  return (
                    <TableRow 
                      key={`${t.id}-${t.kurs_name}`}
                      className={`cursor-pointer hover:bg-gray-100 transition-colors
                        ${t.dozent_kontaktiert ? "bg-green-50 opacity-70" : ""} 
                        ${isVeryUrgent && !t.dozent_kontaktiert ? "bg-red-50" : ""}
                        ${isUrgent && !isVeryUrgent && !t.dozent_kontaktiert ? "bg-orange-50" : ""}`}
                      onClick={() => handleNavigateToTeilnehmer(t.id)}
                    >
                      <TableCell>
                        {t.dozent_kontaktiert ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : isVeryUrgent ? (
                          <Clock className="h-5 w-5 text-red-500 animate-pulse" />
                        ) : (
                          <Clock className="h-5 w-5 text-orange-500" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
                          {t.name || `${t.vorname || ''} ${t.nachname || ''}`.trim() || '-'}
                          <ExternalLink className="h-3 w-3" />
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {t.telefon ? (
                          <a 
                            href={`tel:${t.telefon}`} 
                            className="text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <Phone className="h-3 w-3" />
                            {t.telefon}
                          </a>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Building2 className="h-3 w-3 text-gray-400" />
                          {t.pflegeeinrichtung_name || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          <GraduationCap className="h-3 w-3 mr-1" />
                          {t.kurs_name || t.kurs || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className={`font-medium ${isVeryUrgent && !t.dozent_kontaktiert ? "text-red-600" : isUrgent && !t.dozent_kontaktiert ? "text-orange-600" : ""}`}>
                            {formatDate(t.kurs_start)}
                          </span>
                          {daysUntilStart !== null && !isPast && (
                            <span className={`text-xs ${isVeryUrgent ? "text-red-500 font-medium" : isUrgent ? "text-orange-500" : "text-gray-400"}`}>
                              {daysUntilStart === 0 ? "HEUTE!" : daysUntilStart === 1 ? "Morgen!" : `in ${daysUntilStart} Tagen`}
                            </span>
                          )}
                          {isPast && (
                            <span className="text-xs text-gray-400">vor {Math.abs(daysUntilStart)} Tag(en)</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant={t.dozent_kontaktiert ? "outline" : "default"}
                          onClick={(e) => handleToggleKontaktiert(e, t.id, t.kurs_name, t.dozent_kontaktiert)}
                          className={t.dozent_kontaktiert ? "" : "bg-green-600 hover:bg-green-700"}
                        >
                          {t.dozent_kontaktiert ? (
                            <>
                              <X className="h-4 w-4 mr-1" />
                              Öffnen
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-1" />
                              Erledigt
                            </>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DozentenView;
